import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, FileTextIcon, HistoryIcon, ImageIcon, MapPinIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import type { CitizenRequest, RequestAttachment } from '../../data/demandes';
const attachmentIcons: Record<RequestAttachment['type'], typeof ImageIcon> = {
  image: ImageIcon,
  pdf: FileTextIcon,
  form: FileTextIcon
};
export function RequestDetailsPanel({
  request
}: {
  request: CitizenRequest;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  return <div className="space-y-5" data-id="element-1320" data-mp="2filu1tby0ty6">
      <section aria-labelledby="description-titre" className="rounded-3xl bg-ville-sand p-6 sm:p-7" data-id="element-1321" data-mp="2jhuol0et4vus">
        <h2 id="description-titre" className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-1322" data-mp="2ohz4ao7w5mjy">
          Description de la requête
        </h2>
        <p className="mt-3 text-[16px] leading-relaxed text-slate-700" data-id="element-1323" data-mp="p667arl3sh78">{request.description}</p>
        <p className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-id="element-1324" data-mp="u3ofgfraqse6">
          <SparklesIcon className="h-3.5 w-3.5 text-ville-indigo" aria-hidden="true" data-id="element-1325" data-mp="2k3lrpoxbgmsm" />
          Demande créée par {request.createdBy}
        </p>
      </section>

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-1326" data-mp="2b97fxwbzvue9">
        <section aria-labelledby="emplacement-titre" className="rounded-3xl border border-slate-200 p-6" data-id="element-1327" data-mp="2b7wcjxmgoono">
          <h2 id="emplacement-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-1328" data-mp="2692v5u420di8">
            Emplacement
          </h2>
          <p className="mt-3 flex items-start gap-2 text-[15px] font-bold leading-snug text-ville-navy" data-id="element-1329" data-mp="2z0mm606w6vus">
            <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-1330" data-mp="2tz6z62l5mf9r" />
            {request.address}
          </p>
          <p className="mt-1 pl-6 text-sm text-slate-500" data-id="element-1331" data-mp="2p3rmvf2sorfg">{request.fullAddress}</p>
        </section>

        <section aria-labelledby="dates-titre" className="rounded-3xl border border-slate-200 p-6" data-id="element-1332" data-mp="1iiyv1wq2qvvz">
          <h2 id="dates-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-1333" data-mp="1dol4q8q9rey6">
            Dates
          </h2>
          <dl className="mt-3 space-y-2 text-[15px]" data-id="element-1334" data-mp="1k2qmpprkty3q">
            <div className="flex items-baseline justify-between gap-4" data-id="element-1335" data-mp="1k04hhws5ruoo">
              <dt className="text-slate-500" data-id="element-1336" data-mp="1627n9716g0d6">Créée le</dt>
              <dd className="font-bold tabular-nums text-ville-navy" data-id="element-1337" data-mp="1b1ao0fa4jkfc">{request.createdOn}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4" data-id="element-1338" data-mp="wco3lz5n42h9">
              <dt className="text-slate-500" data-id="element-1339" data-mp="113crz5m0wjc6">Résolue</dt>
              <dd className={`font-bold tabular-nums ${request.resolvedOn ? 'text-ville-navy' : 'text-slate-300'}`} data-id="element-1340" data-mp="114xm4u4fe09i">
                {request.resolvedOn ?? '-- / -- / --'}
              </dd>
            </div>
          </dl>
        </section>
      </div>

      <section aria-labelledby="fichiers-titre" className="rounded-3xl border border-slate-200 p-6 sm:p-7" data-id="element-1341" data-mp="dfd2e90g6yz6">
        <h2 id="fichiers-titre" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-1342" data-mp="iclmif55pd32">
          Fichiers joints
        </h2>

        <ul className="mt-4 flex flex-wrap gap-3" data-id="element-1343" data-mp="3pxw8di2xuvxv">
          {request.attachments.map(file => {
          const Icon = attachmentIcons[file.type];
          return <li key={file.name} data-id="element-1344" data-mp="8ng4dm2wfssu">
                <a href={file.url ?? '#'} target={file.url ? '_blank' : undefined} rel="noreferrer" onClick={event => {
              if (!file.url) event.preventDefault();
            }} title={`${file.name} · ${file.size}`} className="block h-24 w-24 overflow-hidden rounded-2xl border border-slate-200 transition-colors duration-150 ease-ville hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1345" data-mp="3pwnsfiqb3xq">
                  {file.url ? <img src={file.url} alt={`Photo jointe : ${file.name}`} className="h-full w-full object-cover" data-id="element-1346" data-mp="wg4xdiz78g0p" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-ville-mist px-2 text-ville-deep" data-id="element-1347" data-mp="2pvq15njej7jz">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-1348" data-mp="2zn411wf9m9dn" />
                      <span className="w-full truncate text-center text-[11px] font-semibold" data-id="element-1349" data-mp="2zpzyljn7aw1k">{file.name}</span>
                    </span>}
                </a>
              </li>;
        })}
          <li data-id="element-1350" data-mp="2bxt83145pja2">
            <button type="button" className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-slate-300 text-slate-500 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1351" data-mp="2bx9ntdr1aisv">
              <PlusIcon className="h-6 w-6" strokeWidth={1.5} aria-hidden="true" data-id="element-1352" data-mp="7qrj10vyn67" />
              <span className="text-xs font-semibold" data-id="element-1353" data-mp="1kr2ov6yp1ftu">Téléverser</span>
            </button>
          </li>
        </ul>
      </section>

      <section aria-labelledby="historique-titre" className="rounded-3xl border border-slate-200 px-6 py-4 sm:px-7" data-id="element-1354" data-mp="1gq8ze95lrsyu">
        <h2 id="historique-titre" className="sr-only" data-id="element-1355" data-mp="1bwemboki6c8q">
          Historique de la requête
        </h2>
        <button type="button" onClick={() => setHistoryOpen(open => !open)} aria-expanded={historyOpen} className="flex w-full items-center justify-between gap-4 py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1356" data-mp="16x1yb0hwaz4k">
          <span className="inline-flex items-center gap-2 rounded-full bg-ville-mist px-3 py-1.5 text-sm font-bold text-ville-deep" data-id="element-1357" data-mp="t1hg679zqky7">
            <HistoryIcon className="h-4 w-4" aria-hidden="true" data-id="element-1358" data-mp="2nd4mor28w9uf" />
            Historique
          </span>
          <span className="flex items-center gap-2 text-sm text-slate-500" data-id="element-1359" data-mp="2idrzg57gp0rb">
            {request.timeline.length} étapes
            <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-ville ${historyOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1360" data-mp="31hvc172q6zhu" />
          </span>
        </button>

        {historyOpen && <motion.ol initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-4 space-y-4 border-t border-slate-100 pt-4" data-id="element-1361" data-mp="2xhckzf8npxgp">
            {request.timeline.map((step, index) => <li key={step.label} className="flex gap-4" data-id="element-1362" data-mp="3b8zqm5ohoic5" data-mpi={index}>
                <div className="flex flex-col items-center" data-id="element-1363" data-mp="3b8hl0ifxsrrk">
                  <span className={`h-3 w-3 rounded-full ${index === request.timeline.length - 1 ? 'bg-ville-indigo' : 'bg-slate-300'}`} aria-hidden="true" data-id="element-1364" data-mp="3g6rl97giqik1" />
                  {index < request.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-slate-200" aria-hidden="true" data-id="element-1365" data-mp="1x7laurmwg9he" />}
                </div>
                <div className="pb-1" data-id="element-1366" data-mp="1sce9vrl0liw6">
                  <p className="text-sm font-bold text-ville-navy" data-id="element-1367" data-mp="26a2xv8adp65g">{step.label}</p>
                  <p className="text-sm text-slate-500" data-id="element-1368" data-mp="21a8672is4cjc">{step.date}</p>
                </div>
              </li>)}
          </motion.ol>}
      </section>
    </div>;
}